import React, { useEffect, useState } from 'react';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AdminLayout from '../AdminLayout';
import startupService from '../../services/startupService';
import TeamTab from './TeamTable';
import CompetitionTab from './CompetitionTable';

export default function StartupDetailPage() {
    const { startupId } = useParams();
    const navigate = useNavigate();
    const { userProfile } = useAuth();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('basic');
    const [editing, setEditing] = useState(false);
    const [startup, setStartup] = useState(null);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (userProfile?.role !== 'admin') {
            navigate('/');
            return;
        }
        fetchStartup();
    }, [startupId, userProfile, navigate]);

    const fetchStartup = async () => {
        try {
            setLoading(true);
            const data = await startupService.getStartupById(startupId);
            setStartup(data);
            setFormData(data);
            setError('');
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            await startupService.updateStartup(startupId, formData);
            setStartup(formData);
            setEditing(false);
            setError('');
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!userProfile || userProfile.role !== 'admin') {
        return (
            <AdminLayout>
                <div className="p-6 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    Access denied. Admin only.
                </div>
            </AdminLayout>
        );
    }

    if (loading && !startup) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600"></div>
                </div>
            </AdminLayout>
        );
    }

    if (!startup) {
        return (
            <AdminLayout>
                <div className="min-h-screen bg-gray-100 p-6">
                    <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
                        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                            {error || 'Startup not found'}
                        </div>
                        <button
                            onClick={() => navigate('/startups')}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
                        >
                            <BackIcon /> Back to Startups
                        </button>
                    </div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title={startup.name}>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
                <div className="max-w-5xl mx-auto">
                    {error && (
                        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between">
                            <span>{error}</span>
                            <button onClick={() => setError('')} className="font-bold">×</button>
                        </div>
                    )}

                    {/* Header */}
                    <div className="flex justify-between items-center mb-8">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/startups')}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                            >
                                <BackIcon /> Back
                            </button>
                            <h1 className="text-4xl font-bold text-gray-800">{startup.name}</h1>
                        </div>
                        <button
                            onClick={() => (editing ? handleSave() : setEditing(true))}
                            disabled={loading}
                            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading ? '⏳ Saving...' : editing ? '💾 Save Changes' : '✏️ Edit'}
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                        {/* Tab Navigation */}
                        <div className="flex border-b border-gray-300">
                            {['basic', 'team', 'competition'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`flex-1 px-6 py-4 font-semibold transition ${activeTab === tab
                                        ? 'border-b-4 border-red-600 text-red-600 bg-red-50'
                                        : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                >
                                    {tab === 'basic' && '📋 Basic Information'}
                                    {tab === 'team' && '👥 Team Members'}
                                    {tab === 'competition' && '🏆 Competitions'}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="p-8">
                            {/* Basic Information Tab */}
                            {activeTab === 'basic' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Name */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Startup Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name || ''}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                            />
                                        </div>

                                        {/* Founder */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Founder</label>
                                            <input
                                                type="text"
                                                name="founder"
                                                value={formData.founder || ''}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                            />
                                        </div>

                                        {/* Category */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                                            <input
                                                type="text"
                                                name="category"
                                                value={formData.category || ''}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                            />
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email || ''}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                            />
                                        </div>

                                        {/* Phone */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone || ''}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                            />
                                        </div>

                                        {/* Stage */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Stage</label>
                                            <select
                                                name="stage"
                                                value={formData.stage || ''}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                            >
                                                <option value="Idea">Idea</option>
                                                <option value="MVP">MVP</option>
                                                <option value="Growth">Growth</option>
                                                <option value="Scale">Scale</option>
                                            </select>
                                        </div>

                                        {/* Status */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                                            <select
                                                name="status"
                                                value={formData.status || ''}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                            >
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                                <option value="Graduated">Graduated</option>
                                            </select>
                                        </div>

                                        {/* Website URL */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Website URL</label>
                                            <input
                                                type="url"
                                                name="websiteUrl"
                                                value={formData.websiteUrl || ''}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                            />
                                        </div>

                                        {/* LinkedIn URL */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">LinkedIn URL</label>
                                            <input
                                                type="url"
                                                name="linkedinUrl"
                                                value={formData.linkedinUrl || ''}
                                                onChange={handleChange}
                                                disabled={!editing}
                                                className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                            />
                                        </div>
                                    </div>

                                    {/* Problem Statement */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Problem Statement</label>
                                        <textarea
                                            name="problemStatement"
                                            value={formData.problemStatement || ''}
                                            onChange={handleChange}
                                            disabled={!editing}
                                            rows="3"
                                            className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                        ></textarea>
                                    </div>

                                    {/* Solution Summary */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Solution Summary</label>
                                        <textarea
                                            name="solutionSummary"
                                            value={formData.solutionSummary || ''}
                                            onChange={handleChange}
                                            disabled={!editing}
                                            rows="3"
                                            className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                        ></textarea>
                                    </div>

                                    {/* Technology Stack */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Technology Stack</label>
                                        <textarea
                                            name="technologyStack"
                                            value={formData.technologyStack || ''}
                                            onChange={handleChange}
                                            disabled={!editing}
                                            rows="2"
                                            className={`w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${!editing ? 'bg-gray-100' : ''}`}
                                        ></textarea>
                                    </div>
                                </div>
                            )}

                            {/* Team Members Tab */}
                            {activeTab === 'team' && <TeamTab startupId={startupId} />}

                            {/* Competitions Tab */}
                            {activeTab === 'competition' && <CompetitionTab startupId={startupId} />}
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
